import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button'; // Assuming Button component path
import { MessageSquareText, CalendarPlus, ArrowLeft } from 'lucide-react'; // Icons for buttons

import { ShieldCheck, HeartHandshake, Sparkles } from 'lucide-react'; // Icons for illustration

// Refined placeholder for a calming illustration using lucide-react icons
const CalmingIllustration = () => (
  <div className="w-56 h-56 md:w-64 md:h-64 bg-gradient-to-br from-sky-100 to-blue-200 rounded-full flex flex-col items-center justify-center my-8 shadow-lg p-4">
    <div className="flex items-center justify-center space-x-3">
      <HeartHandshake size={48} className="text-blue-500 opacity-90" />
      <ShieldCheck size={64} className="text-sky-600 opacity-90" /> 
      <Sparkles size={32} className="text-blue-400 opacity-90" />
    </div>
    <p className="mt-4 text-sm text-slate-600 text-center">Modern care, at your fingertips.</p>
  </div>
);

export const HelpOptionsScreen: React.FC = () => {
  const navigate = useNavigate();

  const handleTellUsHowYouFeel = () => {
    navigate('/patient/audio-triage/welcome'); // Navigate to the new audio triage welcome screen
  };

  const handleBookAppointment = () => {
    navigate('/patient/appointments/book'); // Navigate to standard booking flow
  };
  
  const handleBackToDashboard = () => {
    navigate('/patient/dashboard'); // Navigate back to the patient dashboard
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-gradient-to-br from-slate-50 to-sky-100 p-6 pt-8">
      <div className="w-full max-w-md md:max-w-2xl mb-4">
        <Button 
          variant="ghost" 
          className="flex items-center text-slate-600 hover:text-slate-900"
          onClick={handleBackToDashboard}
          aria-label="Return to dashboard"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>
      </div>
      
      <h1 className="text-3xl sm:text-4xl font-bold text-slate-700 mb-6 md:mb-8 text-center">How can we help you today?</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full max-w-md md:max-w-2xl mb-10 md:mb-12">
        <Button
          variant="outline"
          className="h-auto py-5 px-6 md:py-6 md:px-8 text-base md:text-lg bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 border-2 border-blue-500 hover:bg-blue-50 text-blue-600 flex flex-col items-center space-y-2 md:space-y-3 rounded-xl"
          onClick={handleTellUsHowYouFeel}
          aria-label="Tell us how you feel and start triage process"
        >
          <MessageSquareText className="w-10 h-10 md:w-12 md:h-12 mb-1 md:mb-2 text-blue-500" />
          <span className="font-semibold">Tell Us How You Feel</span>
          <span className="text-xs text-slate-500">(Start Triage)</span>
        </Button>
        <Button
          variant="outline"
          className="h-auto py-5 px-6 md:py-6 md:px-8 text-base md:text-lg bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 border-2 border-green-500 hover:bg-green-50 text-green-600 flex flex-col items-center space-y-2 md:space-y-3 rounded-xl"
          onClick={handleBookAppointment}
          aria-label="Book an appointment directly"
        >
          <CalendarPlus className="w-10 h-10 md:w-12 md:h-12 mb-1 md:mb-2 text-green-500" />
          <span className="font-semibold">Book an Appointment</span>
          <span className="text-xs text-slate-500">(Direct Booking)</span>
        </Button>
      </div>

      <CalmingIllustration />

      <p className="text-slate-600 text-center max-w-xs sm:max-w-md mx-auto">
        You’re in safe hands. Let’s get you seen as quickly as possible.
      </p>
    </div>
  );
};
